import {
  addDays,
  format,
  isEqual,
  isAfter,
  isBefore,
  differenceInSeconds,
  differenceInDays,
  differenceInMinutes
} from 'date-fns';
import convert, { Unit } from 'convert-units';
import { DataPointOccurrence } from './DataPointOccrence';
import {
  OuraSpecificDataType,
  UserAttribute,
  UserAttributeSource,
  UserAttributeType
} from '../models/UserAttribute';
import api from '../api/api';
import {
  convertSecondsToDate,
  convertSecondsToMinutes,
  fetchGarminData
} from './GarminHelper';
import { log } from './LogHelper';
import { stringify } from 'flatted';
import { logAnalyticsEvent } from '../common/analytics';
import {
  fetchOuraData,
  OuraHearRateData,
  OuraHrvData,
  OuraSleepData,
  OuraSpo2Data,
  OuraStepsData,
  OuraWorkoutData
} from './OuraHelper';

export enum WearableType {
  GOOGLE_FIT = 'Google Fit',
  HEALTH_CONNECT = 'Health Connect',
  HEALTH_KIT = 'HealthKit',
  FITBIT = 'Fitbit',
  GARMIN = 'Garmin',
  OURA = 'Oura'
}

export interface SleepWearableData {
  sleepStage: string;
  value: number;
}
export interface WearableData {
  value: number | string;
  startDate: Date;
  endDate: Date;
  granularity?: any | undefined;
}
export interface WearableResult {
  value: number | string;
  startDate: Date;
  endDate: Date;
  data?: WearableData[] | SleepWearableData[];
  sourceName?: string;
}
export class WearableHelper {
  wearableType: WearableType;
  dataSource: string;
  dataPointOccurrence: DataPointOccurrence;
  dataCategory?: string;
  dataUnit?: string;
  intendedStartDate?: Date;
  userId: number;

  constructor(
    wearableType: WearableType,
    dataSource: string,
    dataPointOccurrence: DataPointOccurrence,
    userId: number,
    dataCategory?: string,
    dataUnit?: string,
    intendedStartDate?: Date
  ) {
    this.wearableType = wearableType;
    this.dataSource = dataSource;
    this.dataPointOccurrence = dataPointOccurrence;
    this.dataCategory = dataCategory;
    this.dataUnit = dataUnit;
    // Make a copy to prevent mutation
    this.intendedStartDate = intendedStartDate
      ? new Date(intendedStartDate)
      : undefined;
    this.userId = userId;
  }

  async authorize(): Promise<boolean> {
    return false;
  }

  async getData(): Promise<any | undefined> {
    if (
      ![WearableType.FITBIT, WearableType.GARMIN, WearableType.OURA].includes(
        this.wearableType
      )
    ) {
      return undefined;
    } else {
      switch (this.dataSource) {
        case 'Weight':
          return this.getWeightData();
        case 'Body Fat Percentage':
          return this.getBodyFatPercentageData();
        case 'Resting Heart Rate':
          return await this.getRestingHeartRateData();
        case 'Oxygen Saturation':
          return this.getOxygenSaturationData();
        case 'Sleep':
          return this.getSleepData();
        case 'Body Mass Index':
          return this.getBmiData();
        case 'HRV':
          return this.getHrvData();
        case 'Step Count':
          return this.getStepCountData();
        case 'Heart Rate':
          return this.getHeartRateData();
        case 'Respiratory Rate':
          return this.getRespiratoryRateData();
        case 'Lowest Heart Rate':
          return this.getLowestHeartRateData();
        case 'Sleep Efficiency':
          return this.getSleepEfficiencyData();
        case 'Awake Time':
          return this.getAwakeTimeData();
        case 'Deep Sleep Duration':
          return this.getDeepSleepDurationData();
        case 'Sleep Latency':
          return this.getSleepLatencyData();
        case 'Light Sleep Duration':
          return this.getLightSleepDurationData();
        case 'REM Sleep Duration':
          return this.getREMSleepDurationData();
        case 'Temperature Deviation':
          return this.getTemperatureDeviationData();
        case 'Temperature Trend Deviation':
          return this.getTemperatureTrendDeviationData();
        case 'Active Calories':
          return this.getActiveCaloriesData();
        case 'High Activity Time':
          return this.getHighActivityTimeData();
        case 'Low Activity Time':
          return this.getLowActivityTimeData();
        case 'Non-wear Time':
          return this.getNonWearTimeData();
        case 'Sedentary Time':
          return this.getSedentaryTimeData();
        case 'SPO2 Nightly Disturbances':
          return this.getSpo2NightlyDisturbancesData();
        case 'Workout':
          return this.getWorkoutData();
        default:
          return undefined;
      }
    }
  }

  async getWeightData(): Promise<number | undefined> {
    return await new Promise<number | undefined>((resolve, reject) => {
      fetchFitBitData('Body', this.userId)
        .then(results => {
          if (results.error) {
            reject(results.message);
            return;
          }
          resolve(
            results
              ? this.getValue(
                  results?.weight?.map((result: any) => {
                    return {
                      endDate: result.date,
                      value: result.weight
                    };
                  }),
                  this.dataPointOccurrence,
                  'kg',
                  this.dataUnit
                )
              : undefined
          );
        })
        .catch(error => reject(error));
    });
  }

  async getBodyFatPercentageData(): Promise<number | undefined> {
    return await new Promise<number | undefined>((resolve, reject) => {
      fetchFitBitData('Body', this.userId)
        .then(results => {
          if (results.error) {
            reject(results.message);
            return;
          }
          resolve(
            results
              ? this.getValue(
                  results?.weight?.map((result: any) => {
                    return {
                      endDate: result.date,
                      value: result.fat
                    };
                  }),
                  this.dataPointOccurrence
                )
              : undefined
          );
        })
        .catch(error => reject(error));
    });
  }

  async getRestingHeartRateData(): Promise<number | undefined> {
    return await new Promise<number | undefined>((resolve, reject) => {
      fetchFitBitData(this.dataSource, this.userId)
        .then(results => {
          if (results.error) {
            reject(results.message);
            return;
          }
          resolve(
            results
              ? this.getValue(
                  results?.['activities-heart']?.map((result: any) => {
                    return {
                      endDate: result.dateTime,
                      value: result.value.restingHeartRate
                    };
                  }),
                  this.dataPointOccurrence
                )
              : undefined
          );
        })
        .catch(error => reject(error));
    });
  }

  async getOxygenSaturationData(): Promise<number | undefined> {
    return await new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.FITBIT) {
        fetchFitBitData(this.dataSource, this.userId)
          .then(results => {
            if (results.error) {
              reject(results.message);
              return;
            }
            resolve(
              results
                ? this.getValue(
                    results?.map((result: any) => {
                      return {
                        endDate: result.dateTime,
                        value: result.value.avg
                      };
                    }),
                    this.dataPointOccurrence
                  )
                : undefined
            );
          })
          .catch(error => reject(error));
      } else if (this.wearableType === WearableType.OURA) {
        fetchOuraData(UserAttributeType.OxygenSaturation, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSpo2Data) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.spo2_percentage?.average
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      } else {
        resolve(undefined);
      }
    });
  }

  async getSleepData(): Promise<WearableResult | undefined> {
    return new Promise<WearableResult | undefined>((resolve, reject) => {
      const handleResults = (results: any) => {
        const result = this.filterGatheredSleepData(
          results,
          this.dataPointOccurrence,
          this.dataCategory
        );
        const value =
          (result?.reduce((result, item) => {
            const duration =
              (!isNaN(item?.value) ? item?.value * 60 : undefined) ||
              differenceInSeconds(
                new Date(item.endDate),
                new Date(item.startDate)
              );
            return result + duration;
          }, 0) || 0) / 60;

        const sleepWearableDataList: SleepWearableData[] = [
          {
            sleepStage: 'AWAKE',
            value: 0
          },
          {
            sleepStage: 'REM',
            value: 0
          },
          {
            sleepStage: `LIGHT`,
            value: 0
          },
          {
            sleepStage: 'DEEP',
            value: 0
          },
          {
            sleepStage: 'ASLEEP',
            value: 0
          }
        ];
        result?.forEach(res => {
          const getValue = (sleepStage: string) => {
            return res.granularity[sleepStage] / 60;
          };
          const awake = sleepWearableDataList.find(
            (data: SleepWearableData) => data.sleepStage === 'AWAKE'
          );
          if (awake) awake.value += getValue('awake');

          const light = sleepWearableDataList.find(
            (data: SleepWearableData) => data.sleepStage === 'LIGHT'
          );
          if (light) light.value += getValue('light');

          const deep = sleepWearableDataList.find(
            (data: SleepWearableData) => data.sleepStage === 'DEEP'
          );
          if (deep) deep.value += getValue('deep');

          const rem = sleepWearableDataList.find(
            (data: SleepWearableData) => data.sleepStage === 'REM'
          );
          if (rem) rem.value += getValue('rem');

          const asleep = sleepWearableDataList.find(
            (data: SleepWearableData) => data.sleepStage === 'ASLEEP'
          );
          if (asleep) asleep.value += getValue('asleep');
        });
        resolve(
          result
            ? {
                value: Number(
                  (value / (this.dataUnit === 'h' ? 60 : 1)).toFixed(2)
                ),
                startDate: new Date(result[result.length - 1].startDate),
                endDate: new Date(result[0].endDate),
                data: sleepWearableDataList?.filter(data => data.value > 0)
              }
            : undefined
        );
      };
      if (this.wearableType === WearableType.FITBIT) {
        fetchFitBitData(this.dataSource, this.userId)
          .then(results => {
            if (!results) {
              resolve(undefined);
              return;
            }
            if (results.error) {
              reject(results.message);
              return;
            }
            handleResults(
              results.sleep.map((data: any) => ({
                endDate: data.endTime,
                startDate: data.startTime,
                value: data.minutesAsleep,
                granularity: {
                  awake: data?.levels?.summary?.wake?.minutes || 0,
                  rem: data?.levels?.summary?.rem?.minutes || 0,
                  light: data?.levels?.summary?.light?.minutes || 0,
                  deep: data?.levels?.summary?.deep?.minutes || 0,
                  asleep: data?.levels?.summary?.asleep?.minutes || 0
                }
              }))
            );
            resolve(undefined);
          })
          .catch(error => reject(error));
      } else if (this.wearableType === WearableType.GARMIN) {
        fetchGarminData(UserAttributeType.Sleep).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results || results?.length <= 0) {
            resolve(undefined);
            return;
          }
          handleResults(
            results.map((result: any) => {
              const data = result.jsonValue;
              const startTimeInSeconds = data.startTimeInSeconds;
              return {
                endDate: convertSecondsToDate(
                  startTimeInSeconds + data.durationInSeconds
                ),
                startDate: convertSecondsToDate(startTimeInSeconds),
                value: convertSecondsToMinutes(data.durationInSeconds),
                granularity: {
                  awake: convertSecondsToMinutes(
                    data?.awakeDurationInSeconds || 0
                  ),
                  rem: convertSecondsToMinutes(data?.remSleepInSeconds || 0),
                  light: convertSecondsToMinutes(
                    data?.lightSleepDurationInSeconds || 0
                  ),
                  deep: convertSecondsToMinutes(
                    data?.deepSleepDurationInSeconds || 0
                  ),
                  asleep: convertSecondsToMinutes(
                    data?.validation === 'MANUAL'
                      ? data.durationInSeconds
                      : data?.unmeasurableSleepInSeconds || 0
                  )
                }
              };
            })
          );
          resolve(undefined);
        });
      } else if (this.wearableType === WearableType.OURA) {
        fetchOuraData(UserAttributeType.Sleep, this.userId).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results || results?.length <= 0) {
            resolve(undefined);
            return;
          }
          handleResults(
            results.map((data: OuraSleepData) => {
              return {
                endDate: new Date(data.bedtime_end),
                startDate: new Date(data.bedtime_start),
                value: convertSecondsToMinutes(data.total_sleep_duration),
                granularity: {
                  awake: convertSecondsToMinutes(data?.awake_time || 0),
                  rem: convertSecondsToMinutes(data?.rem_sleep_duration || 0),
                  light: convertSecondsToMinutes(
                    data?.light_sleep_duration || 0
                  ),
                  deep: convertSecondsToMinutes(data?.deep_sleep_duration || 0)
                }
              };
            })
          );
        });
      } else {
        resolve(undefined);
      }
    });
  }

  async getBmiData(): Promise<number | undefined> {
    return await new Promise<number | undefined>((resolve, reject) => {
      fetchFitBitData('Body', this.userId)
        .then(results => {
          if (results.error) {
            reject(results.message);
            return;
          }
          resolve(
            results
              ? this.getValue(
                  results?.weight?.map((result: any) => {
                    return {
                      endDate: result.date,
                      value: result.bmi
                    };
                  }),
                  this.dataPointOccurrence
                )
              : undefined
          );
        })
        .catch(error => reject(error));
    });
  }

  async getHrvData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.GARMIN) {
        fetchGarminData(UserAttributeType.HeartRateVariability).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((result: any) => {
                  const data = result.jsonValue;
                  const startTimeInSeconds = data.startTimeInSeconds;
                  return {
                    endDate: convertSecondsToDate(
                      startTimeInSeconds + data.durationInSeconds
                    ),
                    startDate: convertSecondsToDate(startTimeInSeconds),
                    value: data.lastNightAvg
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      } else if (this.wearableType === WearableType.FITBIT) {
        fetchFitBitData(this.dataSource, this.userId)
          .then(results => {
            if (!results) {
              resolve(undefined);
              return;
            }
            if (results?.error) {
              reject(results.message);
              return;
            }
            resolve(
              results
                ? this.getValue(
                    results?.hrv?.map((result: any) => {
                      return {
                        endDate: result.dateTime,
                        value: result.value.dailyRmssd
                      };
                    }),
                    this.dataPointOccurrence,
                    'ms',
                    this.dataUnit
                  )
                : undefined
            );
          })
          .catch(error => reject(error));
      } else if (this.wearableType === WearableType.OURA) {
        fetchOuraData(UserAttributeType.HeartRateVariability, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraHrvData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.average_hrv
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      } else {
        resolve(undefined);
      }
    });
  }

  async getStepCountData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.GARMIN) {
        fetchGarminData(UserAttributeType.Steps).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results) {
            resolve(undefined);
            return;
          }
          resolve(
            this.getValue(
              results.map((result: any) => {
                const data = result.jsonValue;
                const startTimeInSeconds = data.startTimeInSeconds;
                return {
                  endDate: convertSecondsToDate(
                    startTimeInSeconds + data.durationInSeconds
                  ),
                  startDate: convertSecondsToDate(startTimeInSeconds),
                  value: data.steps
                };
              }),
              this.dataPointOccurrence
            )
          );
        });
      } else if (this.wearableType === WearableType.FITBIT) {
        fetchFitBitData('Step Count', this.userId)
          .then(results => {
            if (results.error) {
              reject(results.message);
              return;
            }
            resolve(
              results
                ? this.getValue(
                    results?.['activities-steps']?.map((result: any) => {
                      return {
                        endDate: result.dateTime,
                        value: result.value
                      };
                    }),
                    this.dataPointOccurrence
                  )
                : undefined
            );
          })
          ?.catch(error => {
            reject(error);
          });
      } else if (this.wearableType === WearableType.OURA) {
        fetchOuraData(UserAttributeType.Steps, this.userId).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results) {
            resolve(undefined);
            return;
          }
          resolve(
            this.getValue(
              results.map((data: OuraStepsData) => {
                return {
                  endDate: new Date(data.timestamp),
                  startDate: new Date(data.timestamp),
                  value: data.steps
                };
              }),
              this.dataPointOccurrence
            )
          );
        });
      } else {
        resolve(undefined);
      }
    });
  }

  async getHeartRateData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(UserAttributeType.HeartRate, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraHearRateData) => {
                  return {
                    endDate: new Date(data.timestamp),
                    startDate: new Date(data.timestamp),
                    value: data.bpm
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      } else {
        resolve(undefined);
      }
    });
  }

  async getRespiratoryRateData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.RespiratoryRate, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.average_breath
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getLowestHeartRateData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.LowestHeartRate, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.lowest_heart_rate
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getSleepEfficiencyData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.SleepEfficiency, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.efficiency
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getAwakeTimeData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.AwakeTime, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.awake_time
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getDeepSleepDurationData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.DeepSleepDuration, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.deep_sleep_duration
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getSleepLatencyData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.SleepLatency, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.latency
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getLightSleepDurationData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(
          OuraSpecificDataType.LightSleepDuration,
          this.userId
        ).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results) {
            resolve(undefined);
            return;
          }
          resolve(
            this.getValue(
              results.map((data: OuraSleepData) => {
                return {
                  endDate: new Date(data.day),
                  startDate: new Date(data.day),
                  value: data.light_sleep_duration
                };
              }),
              this.dataPointOccurrence
            )
          );
        });
      }
    });
  }

  async getREMSleepDurationData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.REMSleepDuration, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraSleepData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.rem_sleep_duration
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getTemperatureDeviationData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(
          OuraSpecificDataType.TemperatureDeviation,
          this.userId
        ).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results) {
            resolve(undefined);
            return;
          }
          resolve(
            this.getValue(
              results.map((data: OuraSleepData) => {
                return {
                  endDate: new Date(data.day),
                  startDate: new Date(data.day),
                  value: data.readiness.temperature_deviation
                };
              }),
              this.dataPointOccurrence
            )
          );
        });
      }
    });
  }

  async getTemperatureTrendDeviationData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(
          OuraSpecificDataType.TemperatureTrendDeviation,
          this.userId
        ).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results) {
            resolve(undefined);
            return;
          }
          resolve(
            this.getValue(
              results.map((data: OuraSleepData) => {
                return {
                  endDate: new Date(data.day),
                  startDate: new Date(data.day),
                  value: data.readiness.temperature_trend_deviation
                };
              }),
              this.dataPointOccurrence
            )
          );
        });
      }
    });
  }

  async getActiveCaloriesData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.ActiveCalories, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraStepsData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.active_calories
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getHighActivityTimeData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.HighActivityTime, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraStepsData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.high_activity_time
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getLowActivityTimeData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.LowActivityTime, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraStepsData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.low_activity_time
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getNonWearTimeData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.NonWearTime, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraStepsData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.non_wear_time
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getSedentaryTimeData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.SedentaryTime, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraStepsData) => {
                  return {
                    endDate: new Date(data.day),
                    startDate: new Date(data.day),
                    value: data.sedentary_time
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  async getSpo2NightlyDisturbancesData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(
          OuraSpecificDataType.Spo2NightlyDisturbances,
          this.userId
        ).then(results => {
          if (results?.message) {
            reject(results);
            return;
          }
          if (!results) {
            resolve(undefined);
            return;
          }
          resolve(
            this.getValue(
              results.map((data: OuraSpo2Data) => {
                return {
                  endDate: new Date(data.day),
                  startDate: new Date(data.day),
                  value: data.breathing_disturbance_index
                };
              }),
              this.dataPointOccurrence
            )
          );
        });
      }
    });
  }

  async getWorkoutData(): Promise<number | undefined> {
    return new Promise<number | undefined>((resolve, reject) => {
      if (this.wearableType === WearableType.OURA) {
        fetchOuraData(OuraSpecificDataType.Workout, this.userId).then(
          results => {
            if (results?.message) {
              reject(results);
              return;
            }
            if (!results) {
              resolve(undefined);
              return;
            }
            resolve(
              this.getValue(
                results.map((data: OuraWorkoutData) => {
                  return {
                    endDate: new Date(data.end_datetime),
                    startDate: new Date(data.start_datetime),
                    value: data.calories
                  };
                }),
                this.dataPointOccurrence
              )
            );
          }
        );
      }
    });
  }

  getValue(
    results: Array<any>,
    dataPointOccurrence: DataPointOccurrence,
    fromUnit?: string,
    toUnit?: string
  ) {
    const value = this.filterGatheredData(results, dataPointOccurrence)?.[0]
      .value;
    if (value && toUnit) {
      return this.convertValue(value, fromUnit, toUnit);
    }
    return value;
  }

  getTotalValue(
    results: Array<any>,
    dataPointOccurrence: DataPointOccurrence,
    fromUnit?: string,
    toUnit?: string
  ) {
    const value = this.filterGatheredData(results, dataPointOccurrence)?.reduce(
      (partialSum, a) => partialSum + a.value,
      0
    );
    if (value && toUnit) {
      return this.convertValue(value, fromUnit, toUnit);
    }
    return value;
  }

  convertValue(value: number, fromUnit?: string, toUnit?: string): number {
    return Number(
      convert(value)
        .from((fromUnit || 'kg') as Unit)
        .to(toUnit as Unit)
        .toFixed(2)
    );
  }

  filterGatheredData(
    results: Array<any>,
    dataPointOccurrence: DataPointOccurrence,
    dataCategory?: string
  ): Array<any> | undefined {
    if (results.length === 0) {
      return undefined;
    }
    let newResults = results
      ?.filter(result => {
        if (!result?.value) return false;
        const value = result.value;
        if (typeof value === 'string') {
          if (value.length <= 0) return false;
          if (!isNaN(+result.value)) {
            return +result.value > 0;
          }
        } else if (typeof value === 'number') {
          return value > 0;
        }
        return true;
      })
      ?.sort((objA, objB) =>
        isAfter(new Date(objA.endDate), new Date(objB.endDate)) ? -1 : 1
      );
    log(`Raw  Results`, stringify(newResults));
    if (newResults.length === 0) return undefined;
    if (dataPointOccurrence !== DataPointOccurrence.Last_Recorded) {
      newResults = newResults.filter(result => {
        const endDate = new Date(result.endDate);
        endDate.setHours(0, 0, 0, 0);
        const date =
          dataPointOccurrence === DataPointOccurrence.Intended_Start_Date
            ? this.intendedStartDate || new Date()
            : new Date();
        date.setHours(0, 0, 0, 0);
        return (
          (dataCategory
            ? (result.value + '').toLowerCase() === dataCategory.toLowerCase()
            : true) &&
          isEqual(
            addDays(
              date,
              dataPointOccurrence === DataPointOccurrence.Yesterday ? -1 : 0
            ),
            endDate
          )
        );
      });
    } else {
      const endDateString = newResults[0].endDate;
      const date = new Date(endDateString);
      date.setHours(0, 0, 0, 0);
      newResults = newResults.filter(result => {
        const endDate = new Date(result.endDate);
        endDate.setHours(0, 0, 0, 0);
        return (
          (dataCategory
            ? (result.value + '').toLowerCase() === dataCategory.toLowerCase()
            : true) && isEqual(date, endDate)
        );
      });
    }
    log(`Filtered Results`, stringify(newResults));

    return newResults;
  }

  filterGatheredSleepData(
    results: Array<any>,
    dataPointOccurrence: DataPointOccurrence,
    dataCategory?: string
  ): Array<any> | undefined {
    if (!results || results.length === 0) {
      return undefined;
    }
    let newResults = results.sort((objA, objB) =>
      isAfter(new Date(objA.endDate), new Date(objB.endDate)) ? -1 : 1
    );
    log(`Raw Results`, stringify(newResults));

    const endDateString = newResults[0].endDate;
    let minStartDate = new Date(endDateString);
    if (dataPointOccurrence !== DataPointOccurrence.Last_Recorded) {
      minStartDate =
        dataPointOccurrence === DataPointOccurrence.Intended_Start_Date
          ? this.intendedStartDate || new Date()
          : new Date();
    }
    minStartDate.setHours(17, 59, 59, 59);
    minStartDate = addDays(
      minStartDate,
      dataPointOccurrence === DataPointOccurrence.Yesterday ? -2 : -1
    );
    const maxStartDate = addDays(minStartDate, 1);
    const maxEndDate = addDays(minStartDate, 2);
    maxEndDate.setHours(0, 0, 0, 0);
    newResults = newResults.filter(result => {
      const endDate = new Date(result.endDate);
      const startDate = new Date(result.startDate);
      return (
        (dataCategory
          ? result?.value?.toLowerCase() === dataCategory.toLowerCase()
          : true) &&
        isAfter(startDate, minStartDate) &&
        isBefore(startDate, maxStartDate) &&
        isBefore(endDate, maxEndDate)
      );
    });
    const uniqueResults: Array<any> = [];
    newResults.forEach(data => {
      if (
        !uniqueResults.some(
          uniqueData =>
            uniqueData.startDate == data.startDate &&
            uniqueData.endDate == data.endDate
        )
      ) {
        uniqueResults.push(data);
      }
    });
    log(`Filtered Results`, stringify(uniqueResults));
    if (uniqueResults.length === 0) {
      return undefined;
    }
    return uniqueResults;
  }
}

async function fetchFitBitData(
  source: string,
  userId: string | number,
  startDate?: Date,
  endDate?: Date
): Promise<any | undefined> {
  const result = await api.get(
    `fitbit/${userId}/data/${source}/${
      startDate
        ? getFormattedDateString(startDate)
        : getFormattedDateString(addDays(new Date(), -30))
    }/${getFormattedDateString(endDate || new Date())}`,
    {}
  ).response;
  const response = await result.json();
  log(`Results:`, stringify(response));
  if (response.error) {
    logAnalyticsEvent('fetch_fitbit_data_error', {
      userId: userId,
      message: response.message
    });
  }
  return result.ok ? response : { error: true, ...response };
}

function getFormattedDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function handleSleepResult(userId: number, attributes: any[]): UserAttribute[] {
  const res: UserAttribute[] = [];
  if (attributes) {
    attributes.forEach(attribute => {
      res.push({
        userId: userId,
        source: UserAttributeSource.Fitbit,
        startDate: new Date(attribute.startTime),
        endDate: new Date(attribute.endTime),
        type: UserAttributeType.Sleep,
        unit: 'h',
        numberValue:
          differenceInMinutes(
            new Date(attribute.endTime),
            new Date(attribute.startTime)
          ) / 60,
        jsonValue: attribute
      });
    });
  }
  return res;
}

function sortData(data: UserAttribute[], order: 'ASC' | 'DESC') {
  return data.sort((a, b) =>
    order === 'DESC'
      ? b.startDate.getTime() - a.startDate.getTime()
      : a.startDate.getTime() - b.startDate.getTime()
  );
}

export async function getLastSync(source: UserAttributeSource, userId: number) {
  try {
    const result = await api.get(
      `users/${userId}/attributes/last?source=${source}`,
      {}
    );
    const response = await result.response;
    const lastSync: UserAttribute = await response.json();
    return lastSync;
  } catch (error) {
    log(stringify(error));
    return undefined;
  }
}

async function getSleepData(userId: number, startDate: Date, endDate?: Date) {
  const data = await fetchFitBitData('Sleep', userId, startDate, endDate);
  if (data.error) {
    throw data.message;
  }
  return handleSleepResult(userId, data.sleep);
}

async function getHRVData(
  userId: number,
  startDate: Date,
  endDate?: Date
): Promise<UserAttribute[] | undefined> {
  const data = await fetchFitBitData('HRV', userId, startDate, endDate);
  if (data.error) {
    throw data.message;
  }
  if (data.hrv) {
    return data.hrv.map(
      (hrv: {
        value: { dailyRmssd: number; deepRmssd: number };
        dateTime: string;
      }) => ({
        userId: userId,
        source: UserAttributeSource.Fitbit,
        startDate: new Date(hrv.dateTime),
        endDate: new Date(hrv.dateTime),
        type: UserAttributeType.HeartRateVariability,
        unit: 'ms',
        numberValue: hrv.value.dailyRmssd,
        jsonValue: hrv
      })
    );
  } else {
    return undefined;
  }
}

async function getStepCountData(
  userId: number,
  startDate: Date,
  endDate?: Date
): Promise<UserAttribute[] | undefined> {
  const data = await fetchFitBitData('Step Count', userId, startDate, endDate);
  return data?.['activities-steps']?.map(
    (steps: { value: number; dateTime: string }) => ({
      userId: userId,
      source: UserAttributeSource.Fitbit,
      startDate: new Date(steps.dateTime),
      endDate: new Date(steps.dateTime),
      type: UserAttributeType.Steps,
      unit: 'steps',
      numberValue: steps.value,
      jsonValue: steps
    })
  );
}

async function getOxygenSaturationData(
  userId: number,
  startDate: Date,
  endDate?: Date
): Promise<UserAttribute[] | undefined> {
  const data = await fetchFitBitData(
    'Oxygen Saturation',
    userId,
    startDate,
    endDate
  );
  return data?.map((data: any) => ({
    userId: userId,
    source: UserAttributeSource.Fitbit,
    startDate: new Date(data.dateTime),
    endDate: new Date(data.dateTime),
    type: UserAttributeType.OxygenSaturation,
    unit: '%',
    numberValue: data.value.avg,
    jsonValue: data
  }));
}

async function getRestingHeartRateData(
  userId: number,
  startDate: Date,
  endDate?: Date
): Promise<UserAttribute[] | undefined> {
  const data = await fetchFitBitData(
    'Resting Heart Rate',
    userId,
    startDate,
    endDate
  );
  return data?.['activities-heart']?.map((data: any) => ({
    userId: userId,
    source: UserAttributeSource.Fitbit,
    startDate: new Date(data.dateTime),
    endDate: new Date(data.dateTime),
    type: UserAttributeType.RestingHeartRate,
    unit: 'bpm',
    numberValue: data.value.restingHeartRate,
    jsonValue: data
  }));
}

async function getBodyData(
  userId: number,
  startDate: Date,
  endDate?: Date
): Promise<UserAttribute[] | undefined> {
  const data = await fetchFitBitData('Body', userId, startDate, endDate);
  const res: any = [];
  data?.['weight']?.forEach((data: any) => {
    if (typeof data.bmi === 'number') {
      res.push({
        userId: userId,
        source: UserAttributeSource.Fitbit,
        startDate: new Date(`${data.date}T${data.time}`),
        endDate: new Date(`${data.date}T${data.time}`),
        type: UserAttributeType.BodyMassIndex,
        numberValue: data.bmi,
        jsonValue: data
      });
    }

    if (typeof data.fat === 'number') {
      res.push({
        userId: userId,
        source: UserAttributeSource.Fitbit,
        startDate: new Date(`${data.date}T${data.time}`),
        endDate: new Date(`${data.date}T${data.time}`),
        type: UserAttributeType.BodyFatPercentage,
        numberValue: data.fat,
        unit: '%',
        jsonValue: data
      });
    }

    if (typeof data.weight === 'number') {
      res.push({
        userId: userId,
        source: UserAttributeSource.Fitbit,
        startDate: new Date(`${data.date}T${data.time}`),
        endDate: new Date(`${data.date}T${data.time}`),
        type: UserAttributeType.Weight,
        numberValue: data.weight,
        unit: 'kg',
        jsonValue: data
      });
    }
  });

  return res;
}

export async function getFitbitDataToSynchronize(
  userId: number,
  storeUserId: number = userId
) {
  const today = new Date();
  const lastSync = await getLastSync(UserAttributeSource.Fitbit, storeUserId);
  let startDate =
    lastSync && differenceInDays(today, new Date(lastSync.startDate)) < 90
      ? new Date(lastSync.startDate)
      : addDays(new Date(), -90);

  const data: UserAttribute[] = [];

  const sleepData = await getSleepData(userId, startDate);
  if (sleepData) data.push(...sanitizeData(sleepData, startDate));

  const stepCountData = await getStepCountData(userId, startDate);
  if (stepCountData) data.push(...sanitizeData(stepCountData, startDate));

  const oxygenSaturationData = await getOxygenSaturationData(userId, startDate);
  if (oxygenSaturationData)
    data.push(...sanitizeData(oxygenSaturationData, startDate));

  const restingHeartRateData = await getRestingHeartRateData(userId, startDate);
  if (restingHeartRateData)
    data.push(...sanitizeData(restingHeartRateData, startDate));

  // Fetch records by batch as Max date range for fetching HRV & Body data is 30 days
  while (startDate < today) {
    const chunkEndDate = addDays(startDate, 30);
    const endDate = chunkEndDate > today ? today : chunkEndDate;

    const bodyData = await getBodyData(userId, startDate, endDate);
    if (bodyData) data.push(...sanitizeData(bodyData, startDate));

    const hrvData = await getHRVData(userId, startDate, endDate);
    if (hrvData) data.push(...sanitizeData(hrvData, startDate));

    startDate = endDate;
    if (startDate < endDate) {
      // Add delay to throttle API call when syncing more than 30 days of data
      await new Promise(res => setTimeout(res, 5000));
    }
  }

  return sortData(data, 'ASC').map(row => ({ ...row, userId: storeUserId }));
}

function sanitizeData(
  attributes: UserAttribute[],
  startDate: Date
): UserAttribute[] {
  return attributes.filter(({ startDate: dataStartDate, numberValue }) => {
    // Exclude already-synced data
    if (!isAfter(dataStartDate, startDate)) return false;
    if (numberValue == null) return false;
    return numberValue > 0;
  });
}

export async function revokeFitbitAuthorization(
  userId: number,
  clientId: string
): Promise<void> {
  try {
    const result = await api.post(`fitbit/${userId}/revoke`, { clientId });
    const response = await result.response;
    log(`revokeFitbitAuthorization results:`, response ? 'success' : 'failed');
  } catch (error) {
    log(`revokeFitbitAuthorization error:`, stringify(error));
    throw error;
  }
}
